/**
 * 聊天 Hook（重构版）
 *
 * @description
 * 这是组合式 Hook，将聊天逻辑拆分为多个单一职责的子 Hooks：
 * - useChatCore: 核心聊天逻辑（发送、停止、加载状态）
 * - useThread: 会话管理（加载、删除）
 *
 * 符合 SDUI 原则：Backend -> SSE -> EventHandler -> Store
 *
 * @returns {
 *   sendMessage: 发送消息函数
 *   messages: 消息列表
 *   isStreaming: 是否正在流式输出
 *   isLoading: 是否正在加载
 *   error: 错误信息
 *   inputMessage: 输入消息
 *   setInputMessage: 设置输入消息
 *   stopGeneration: 停止生成
 *   loadThread: 加载历史会话
 *   deleteThread: 删除会话
 *   retry: 重试最后一条消息
 * }
 *
 * @example
 * ```typescript
 * const { sendMessage, messages, isStreaming, stopGeneration } = useChat()
 * await sendMessage('你好，帮我搜索信息')
 * stopGeneration() // 取消正在发送的消息
 * ```
 */

import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useChatCore } from './chat/useChatCore'
import { useThread } from './chat/useThread'

import {
  useInputMessage,
  useSetInputMessageAction,
} from '@/hooks/useChatSelectors'

export interface UseChatOptions {
  /**
   * 线程 URL 基座：首条消息创建线程后 replace 导航的目标前缀。
   * 默认 '/chat'（独立聊天页）；工作台传 '/workbench' 实现页内换线程。
   */
  threadUrlBase?: string
  /**
   * 流中断（服务端任务仍在执行）时的回调，参数为该 run id。
   * 传了它就表示上层愿意接管现场（工作台用它启动运行状态轮询）；
   * 不传则维持「中断即报错、结果需手动刷新」的旧行为。
   */
  onStreamInterrupted?: (runId: string) => void
}

export function useChat({ threadUrlBase = '/chat', onStreamInterrupted }: UseChatOptions = {}) {
  const navigate = useNavigate()

  const inputMessage = useInputMessage()
  const setInputMessage = useSetInputMessageAction()

  // 1. Get chat core logic with callbacks
  const chatCore = useChatCore({
    onStreamInterrupted,
    onNewThread: useCallback((threadId: string) => {
      // 🔥 修复：保留 isNew 状态，避免触发不必要的 loadThread
      // 后端已创建会话，标记 isNew: false 表示会话已存在
      navigate(`${threadUrlBase}/${threadId}`, {
        replace: true,
        state: { isNew: false }
      })
    }, [navigate, threadUrlBase]),
  })

  // 2. Get thread manager
  const threadManager = useThread()

  // 3. Retry last user message (重新发送最后一条用户消息)
  const retry = useCallback(() => {
    const lastMessage = threadManager.messages.filter(m => m.role === 'user').pop()
    if (lastMessage?.content) {
      chatCore.sendMessage(lastMessage.content)
    }
  }, [threadManager.messages, chatCore])

  // 4. Regenerate AI response (重新生成指定 AI 消息的回复，不重复添加用户消息)
  const regenerate = useCallback((messageId: string | number) => {
    chatCore.regenerateMessage(messageId)
  }, [chatCore])

  return {
    // ========== State ==========
    messages: threadManager.messages,
    inputMessage,
    isStreaming: chatCore.isGenerating,
    threadMode: chatCore.threadMode,

    // ========== Methods ==========
    sendMessage: chatCore.sendMessage,
    setInputMessage,
    stopGeneration: chatCore.stopGeneration,
    detachActiveStream: chatCore.detachActiveStream,
    resumeExecution: chatCore.resumeExecution,

    // Thread management
    deleteThread: threadManager.deleteThread,

    // Retry / Regenerate
    retry,
    regenerate,
  }
}
